import moment from 'moment';

/** Fraction of each x-step used by the bar; remainder is visible gap. */
export const FLOW_VOLUME_BAR_FILL_RATIO = 0.62;

/** y-axis width + ComposedChart margins + XAxis side padding. */
export const FLOW_VOLUME_CHART_HORIZONTAL_INSET = 96;

export const FLOW_VOLUME_BAR_RADIUS = [4, 4, 0, 0];

export function computeBarSize(pointCount, chartWidth, fillRatio = FLOW_VOLUME_BAR_FILL_RATIO) {
  if (!pointCount || !chartWidth) {
    return 16;
  }

  const plotWidth = Math.max(chartWidth - FLOW_VOLUME_CHART_HORIZONTAL_INSET, 80);
  const step = pointCount > 1 ? plotWidth / (pointCount - 1) : plotWidth;
  const size = Math.floor(step * fillRatio);

  return Math.max(4, Math.min(size, 52));
}

/** Width per bar when multiple series share the same x tick (e.g. RO inlet + permeate). */
export function computeGroupedBarSize(
  pointCount,
  chartWidth,
  barsPerGroup = 2,
  fillRatio = FLOW_VOLUME_BAR_FILL_RATIO,
) {
  const groupWidth = computeBarSize(pointCount, chartWidth, fillRatio);
  const innerGap = 2;

  if (barsPerGroup <= 1) {
    return groupWidth;
  }

  const perBar = Math.floor((groupWidth - innerGap * (barsPerGroup - 1)) / barsPerGroup);
  return Math.max(4, Math.min(perBar, 52));
}

export const FLOW_VOLUME_DENSE_POINT_THRESHOLD = 48;
export const FLOW_VOLUME_MIN_CATEGORY_WIDTH = 18;

/** Minimum inner plot width so dense bar charts stay readable (enables horizontal scroll). */
export function getFlowVolumePlotWidth(pointCount, chartWidth) {
  if (pointCount <= FLOW_VOLUME_DENSE_POINT_THRESHOLD) {
    return chartWidth;
  }

  return Math.max(chartWidth, pointCount * FLOW_VOLUME_MIN_CATEGORY_WIDTH);
}

export function isDenseFlowVolumeChart(pointCount) {
  return pointCount > FLOW_VOLUME_DENSE_POINT_THRESHOLD;
}

/** Format one x tick from bucket metadata (chronological, no skipped-index artifacts). */
export function formatFlowVolumeTickFromPoint(point, totalPoints) {
  if (!point) {
    return '';
  }

  if (point.timestamp != null) {
    const tickMoment = moment.unix(point.timestamp);

    if (totalPoints > 72) {
      return tickMoment.format('MMM D');
    }

    if (totalPoints > 36) {
      return tickMoment.format('MMM D HH:mm');
    }

    return `${tickMoment.format('MMM D')}\n${tickMoment.format('HH:mm')}`;
  }

  return point.name || '';
}

/** @deprecated Prefer formatFlowVolumeTickFromPoint */
export function formatFlowVolumeXAxisLabel(label, index, data = []) {
  return formatFlowVolumeTickFromPoint(data[index], data.length) || label;
}

export function getFlowVolumeChartMargin(pointCount, densePlot = false) {
  if (densePlot) {
    return { top: 10, right: 16, left: 0, bottom: 18 };
  }
  if (pointCount > 36) {
    return { top: 10, right: 10, left: 0, bottom: 16 };
  }
  if (pointCount > 24) {
    return { top: 10, right: 10, left: 0, bottom: 14 };
  }
  return { top: 10, right: 10, left: 0, bottom: 4 };
}

/** Estimate how many x labels fit without overlapping. */
export function getXAxisInterval(pointCount, chartWidth = 600, labelWidth = 64) {
  if (pointCount <= 1) {
    return 0;
  }

  const plotWidth = Math.max(chartWidth - FLOW_VOLUME_CHART_HORIZONTAL_INSET, 80);
  const maxVisibleLabels = Math.max(3, Math.floor(plotWidth / labelWidth));

  if (pointCount <= maxVisibleLabels) {
    return 0;
  }

  return Math.ceil(pointCount / maxVisibleLabels) - 1;
}

const FLOW_VOLUME_DAY_FORMATS = ['MMM DD', 'MMM D', 'MMM DD YYYY', 'MMM D YYYY'];
const FLOW_VOLUME_TIME_FORMATS = [
  'MMM DD HH:mm',
  'MMM D HH:mm',
  'MMM DD HH:mm YYYY',
  'MMM D HH:mm YYYY',
];

const hasYearInLabel = label => /\b(19|20)\d{2}\b/.test(label);
const hasTimeInLabel = (startRaw, endRaw) =>
  /\d{1,2}:\d{2}/.test(startRaw) || /\d{1,2}:\d{2}/.test(endRaw);

const applyParsedDateParts = (target, parsed, includeTime) => {
  if (!parsed.isValid()) {
    return target;
  }

  target.month(parsed.month()).date(parsed.date());

  if (includeTime) {
    target
      .hour(parsed.hour())
      .minute(parsed.minute())
      .second(0)
      .millisecond(0);
  }

  return target;
};

const parseBucketMoments = (point, startRaw, endRaw) => {
  const hasTime = hasTimeInLabel(startRaw, endRaw);
  const hasYear = hasYearInLabel(`${startRaw} ${endRaw}`);
  const formats = hasTime ? FLOW_VOLUME_TIME_FORMATS : FLOW_VOLUME_DAY_FORMATS;

  if (point?.timestamp != null && !hasYear) {
    const start = moment.unix(point.timestamp);
    const parsedStart = moment(startRaw, formats, true);
    const parsedEnd = moment(endRaw, formats, true);

    if (parsedStart.isValid()) {
      applyParsedDateParts(start, parsedStart, hasTime);
    }

    let end = start.clone();
    if (parsedEnd.isValid()) {
      end = applyParsedDateParts(end, parsedEnd, hasTime);
      if (end.isSameOrBefore(start)) {
        end.add(1, hasTime ? 'day' : 'year');
      }
    }

    return { start, end };
  }

  return {
    start: moment(startRaw, formats, true),
    end: moment(endRaw, formats, true),
  };
};

const formatTooltipDate = (value, includeYear) =>
  includeYear ? value.format('MMM D, YYYY') : value.format('MMM D');

/** Parse bucket range label into a title + optional subtitle for chart tooltips. */
export function parseFlowVolumeTooltipRange(point) {
  const label = point?.tooltipLabel || point?.name || '';
  const [startRaw, endRaw] = label.split(' to ').map(part => part.trim());

  if (!startRaw || !endRaw) {
    return { title: label, subtitle: null };
  }

  const hasTime = hasTimeInLabel(startRaw, endRaw);
  const hasYear = hasYearInLabel(label);
  const { start, end } = parseBucketMoments(point, startRaw, endRaw);

  if (!start.isValid() || !end.isValid()) {
    return { title: label, subtitle: null };
  }

  const includeYear = hasYear || start.year() !== end.year();

  if (hasTime && start.format('YYYY-MM-DD') === end.format('YYYY-MM-DD')) {
    return {
      title: formatTooltipDate(start, includeYear),
      subtitle: `${start.format('h:mm A')} – ${end.format('h:mm A')}`,
    };
  }

  if (hasTime) {
    return {
      title: `${formatTooltipDate(start, includeYear)}, ${start.format(
        'h:mm A',
      )} – ${formatTooltipDate(end, includeYear)}, ${end.format('h:mm A')}`,
      subtitle: null,
    };
  }

  if (start.format('YYYY-MM-DD') === end.format('YYYY-MM-DD')) {
    return { title: formatTooltipDate(start, includeYear), subtitle: 'Daily total' };
  }

  return {
    title: `${formatTooltipDate(start, includeYear)} – ${formatTooltipDate(end, includeYear)}`,
    subtitle: null,
  };
}
